import {
  getTranslatedText,
  BackgroundIcon,
  Loader,
  preFormatForm,
  advancedSearchConstraints,
  validateForm
} from '@nile/components';
import { useQuery } from '@apollo/client';
import AdvancedSearch from '@nile/components/src/Components/AdvancedSearch/AdvancedSearch';
import {
  GET_ENTITY_PROPERTIES,
  GET_RULEBUILDER_OPERATORS,
  AccountClient
} from '@nile/graphql';
import {
  Sheet,
  Button,
  ButtonGroup,
  Heading
} from '@superops/ui-design-system';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import envConfig from '@nile/home/src/Config/env';
import { SVGIcon } from '@superops/super-icons';

const ResetButton = styled.span`
  position: absolute;
  top: -6px;
  right: -6px;
  z-index: 12;
  cursor: pointer;
`;

const noop = () => {};

function SearchFilter(props) {
  const {
    intl,
    updateCondition,
    conditions,
    validateFilterConditions = false,
    iconOnly = false,
    prefilledRule
  } = props;

  const [sheetActive, setSheetActive] = useState(false);
  const toggleSheetActive = useCallback(
    () => setSheetActive((active) => !active),
    []
  );

  // Auto-open sheet when prefilledRule is set
  useEffect(() => {
    if (prefilledRule) {
      setSheetActive(true);
    }
  }, [prefilledRule]);

  const haveConditions = conditions && conditions?.operands?.length > 0;
  const handleDone = (condition) => {
    updateCondition(condition);
    setSheetActive(false);
  };
  return (
    <div className="advanced-filter position-relative">
      {props.showResetOption && haveConditions && (
        <ResetButton onClick={() => props.onClickReset()}>
          <SVGIcon name="CircleCancelMinor" size="sm" color="#FF7A7A" />
        </ResetButton>
      )}
      {iconOnly ? (
        <Button
          {...(iconOnly && { tertiary: true })}
          onClick={toggleSheetActive}
          icon={<SVGIcon name="Filter" />}
        >
          {haveConditions && (
            <CountBulb className={`${!iconOnly && 'ml8'} f-12`}>
              {conditions?.operands?.length}
            </CountBulb>
          )}
        </Button>
      ) : (
        <Button
          {...(iconOnly && { tertiary: true })}
          onClick={toggleSheetActive}
          icon={<SVGIcon name="Filter" />}
        >
          {!iconOnly && <FormattedMessage id="ticket.search.filter" />}
          {haveConditions && (
            <CountBulb className={`${!iconOnly && 'ml8'} f-12`}>
              {conditions?.operands?.length}
            </CountBulb>
          )}
        </Button>
      )}
      {/* <Button
        {...(iconOnly && { tertiary: true })}
        onClick={toggleSheetActive}
        icon={<SVGIcon name="Filter" />}
      >
        {!iconOnly ? <FormattedMessage id="ticket.search.filter" /> : null}
        {haveConditions && (
          <CountBulb className={`${!iconOnly && 'ml8'} f-12`}>
            {conditions?.operands?.length}
          </CountBulb>
        )}
      </Button> */}
      <Sheet open={sheetActive} onClose={toggleSheetActive}>
        <FilterSection
          intl={intl}
          conditions={conditions}
          handleClose={toggleSheetActive}
          handleDone={handleDone}
          updateAdvancedConditions={updateCondition}
          validateFilterConditions={validateFilterConditions}
          {...props}
        />
      </Sheet>
    </div>
  );
}

export function FilterSection(props) {
  const {
    meta,
    handleDone,
    handleClose,
    listMeta,
    currentModule,
    onAttributeSelect,
    onValueSearch,
    conditions,
    isNewBuilder,
    validateFilterConditions,
    renderOnlyFilterSection,
    getValueGroupAccessor,
    getCustomGroupValueLabel,
    prefilledRule,
    onRulePrefilled,
    customValidation,
    dependentFields
  } = props;

  const intl = useIntl();

  const [condition, setCondition] = useState(
    conditions || {
      joinOperator: 'AND',
      operands: []
    }
  );

  const [errors, setErrors] = useState(null);

  useEffect(() => {
    setCondition(conditions || condition);
  }, [JSON.stringify(conditions)]);

  const {
    loading: operatorsLoading,
    error: operatorsError,
    data: allOperators
  } = useQuery(GET_RULEBUILDER_OPERATORS);
  const {
    loading: entitiesLoading,
    error: entitiesError,
    data: allEntities
  } = useQuery(GET_ENTITY_PROPERTIES, { client: AccountClient });

  const handleApply = useCallback(() => {
    const conditionParams = condition.operands.length
      ? condition
      : {
          joinOperator: 'AND',
          operands: []
        };

    // Run standard form validation if enabled
    if (validateFilterConditions) {
      const validationResult = validateForm(
        condition,
        advancedSearchConstraints
      );
      if (validationResult) {
        setErrors(validationResult?.operands);
        return;
      }
    }

    // Run custom validation (e.g., software version dependency check)
    if (customValidation && typeof customValidation === 'function') {
      const customValidationResult = customValidation(conditionParams);
      if (!customValidationResult) {
        // Custom validation failed, don't close the sheet
        return;
      }
    }

    setErrors(null);
    handleDone(conditionParams);
  }, [condition, validateFilterConditions, customValidation, handleDone]);

  useEffect(() => {
    if (!renderOnlyFilterSection) return;
    handleApply();
  }, [renderOnlyFilterSection, handleApply]);

  const loading = operatorsLoading || entitiesLoading;

  const filterSection = loading ? (
    <Loader />
  ) : (
    <AdvancedSearch
      query={condition}
      meta={listMeta}
      allEntities={allEntities}
      allOperators={allOperators}
      currentModule={currentModule}
      context={{
        updateAutomationRule: (c) => {
          setCondition(c);
        }
      }}
      onAttributeSelect={onAttributeSelect}
      onValueSearch={onValueSearch}
      isNewBuilder={isNewBuilder}
      errors={errors ?? props.errors}
      getValueGroupAccessor={getValueGroupAccessor}
      getCustomGroupValueLabel={getCustomGroupValueLabel}
      prefilledRule={prefilledRule}
      onRulePrefilled={onRulePrefilled}
      dependentFields={dependentFields}
    />
  );

  if (renderOnlyFilterSection) {
    return (
      <>
        {React.cloneElement(
          filterSection,
          loading
            ? {}
            : {
                // onAttributeSelect: (...args) => {
                //   handleApply();
                //   return onAttributeSelect(...args);
                // },
                // onValueSearch: (...args) => {
                //   onValueSearch(...args);
                //   // handleApply();
                // }
              }
        )}
        {!loading && props.children}
      </>
    );
  }

  return (
    <FilterWrapper className="p20">
      <>
        <div className="flex mb25" data-qa="searchFilter">
          <Heading element="h1">
            {meta?.heading || getTranslatedText(intl, 'ticket.search.filter')}
          </Heading>
          <div className="mlauto">
            <ButtonGroup className="fx mlauto">
              {/* <Button plain onClick={handleClear}>
              <FormattedMessage id="ticket.search.reset" />
            </Button> */}
              <Button plain onClick={handleClose}>
                <FormattedMessage id="common.close" />
              </Button>
              <Button primary onClick={handleApply}>
                <FormattedMessage id="ticket.search.apply" />
              </Button>
            </ButtonGroup>
          </div>
        </div>
        {filterSection}
        {props.children}
      </>
    </FilterWrapper>
  );
}

const FilterWrapper = styled.div`
  width: 50vw;
  background: #fff;
  min-height: 100vh;
  .Polaris-ButtonGroup {
    margin-left: auto;
  }
`;

const CountBulb = styled.div`
  display: inline-block;
  color: #fff;
  border-radius: 4px;
  background-color: #333;
  min-width: 20px;
  text-align: center;
`;

export default injectIntl(SearchFilter);
